import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import "./RegulatoryInfo.css";

const SECTIONS = [
  {
    id: "registration",
    num: "01",
    icon: "fa-building-columns",
    title: "Registration & Licensing",
    color: "#2563eb",
    type: "registration",
  },
  {
    id: "services",
    num: "02",
    icon: "fa-briefcase",
    title: "Nature of Services",
    color: "#0891b2",
    type: "bullets",
    intro: "Wealth Compounders provides services which may include:",
    bullets: [
      "Financial planning",
      // "Investment advisory (equities, mutual funds, etc.)",
      // "Portfolio review and wealth management strategies",
      // "Educational content related to trading and investing",
    ],
    note: "All services are offered in accordance with SEBI (Investment Advisers) Regulations, 2013 and other applicable rules.",
  },
  {
    id: "risk",
    num: "03",
    icon: "fa-triangle-exclamation",
    title: "Risk Disclosure",
    color: "#f59e0b",
    type: "bullets",
    intro:
      "Investments in securities markets are subject to market risks, including potential loss of principal.",
    bullets: [
      "Past performance is not indicative of future returns",
      "Clients are advised to carefully consider their risk appetite",
      "No guarantee of returns or capital protection is provided",
    ],
  },
  // {
  //   id: "execution",
  //   num: "04",
  //   icon: "fa-ban",
  //   title: "No Execution / Distribution",
  //   color: "#7c3aed",
  //   type: "text",
  //   content:
  //     "Wealth Compounders does not execute trades or hold client funds/securities directly. Investment execution may be done through registered brokers or platforms chosen by the client.",
  // },
  {
    id: "conflict",
    num: "04",
    icon: "fa-handshake",
    title: "Conflict of Interest",
    color: "#10b981",
    type: "text",
    content:
      "Wealth Compounders maintains policies to manage conflicts of interest and acts in the best interest of clients. Any potential conflicts will be disclosed transparently.",
  },
  {
    id: "fees",
    num: "05",
    icon: "fa-indian-rupee-sign",
    title: "Fees & Charges",
    color: "#0891b2",
    type: "bullets",
    bullets: [
      // "Fees for advisory/consulting services are communicated upfront",
      "No hidden charges",
      "Fee structure complies with SEBI guidelines",
    ],
  },
  {
    id: "grievance",
    num: "06",
    icon: "fa-headset",
    title: "Grievance Redressal",
    color: "#ef4444",
    type: "grievance",
  },
  {
    id: "compliance",
    num: "07",
    icon: "fa-shield-halved",
    title: "Compliance & Audit",
    color: "#8b5cf6",
    type: "bullets",
    intro: "Wealth Compounders adheres to:",
    bullets: [
      "SEBI regulations",
      "Anti-Money Laundering (AML) guidelines",
      "Know Your Customer (KYC) norms",
    ],
    note: "Regular internal and external audits may be conducted as required.",
  },
  {
    id: "privacy",
    num: "08",
    icon: "fa-lock",
    title: "Data Privacy & Confidentiality",
    color: "#10b981",
    type: "text",
    content:
      "Client information is kept strictly confidential and used only for advisory purposes, in compliance with applicable data protection laws.",
  },
  {
    id: "disclaimer",
    num: "09",
    icon: "fa-circle-info",
    title: "Disclaimer",
    color: "#2563eb",
    type: "text",
    content:
      "This document is for informational purposes only and does not constitute an offer to buy/sell securities. Clients are advised to read all related documents carefully before investing.",
  },
];

const STATS = [
  { icon: "fa-building-columns", val: "SEBI", label: "Regulated" },
  { icon: "fa-list-ol", val: "10", label: "Sections" },
  { icon: "fa-shield-halved", val: "AML/KYC", label: "Compliant" },
  { icon: "fa-rotate", val: "2025", label: "Updated" },
];

export default function RegulatoryInfo() {
  const navigate = useNavigate();
  const [active, setActive] = useState("registration");
  const rightPanelRef = useRef(null);

  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) return;
    const handler = () => {
      const scrollTop = panel.scrollTop + 60;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById("ri-" + SECTIONS[i].id);
        if (el && el.offsetTop <= scrollTop) {
          setActive(SECTIONS[i].id);
          break;
        }
      }
    };
    panel.addEventListener("scroll", handler, { passive: true });
    return () => panel.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    const panel = rightPanelRef.current;
    const el = document.getElementById("ri-" + id);
    if (panel && el) {
      panel.scrollTo({ top: el.offsetTop - 32, behavior: "smooth" });
      setActive(id);
    }
  };

  const renderBody = (s) => {
    if (s.type === "registration") {
      return (
        <>
          <p className="wri-text">
            Wealth Compounders operates in compliance with applicable Indian
            financial regulations. Depending on the services offered, the firm
            may be registered with:
          </p>
          <div className="wri-reg-card">
            <div className="wri-reg-header">
              <div className="wri-reg-header-icon">
                <i className="fas fa-building-columns" />
              </div>
              <span>Securities and Exchange Board of India (SEBI) as:</span>
            </div>
            <div className="wri-reg-roles">
              {[
                { label: "Investment Adviser", tag: "RIA" },
                // { label: "Research Analyst", tag: "RA" },
                { label: "Portfolio Manager", tag: "PMS" },
              ].map((r) => (
                <div key={r.tag} className="wri-reg-role">
                  <i
                    className="fas fa-check-circle"
                    style={{ color: "#2563eb" }}
                  />
                  <span className="wri-reg-role-label">{r.label}</span>
                  <span className="wri-reg-role-tag">{r.tag}</span>
                </div>
              ))}
            </div>
            <div className="wri-reg-number">
              <i className="fas fa-id-badge" />
              <span className="wri-reg-num-label">SEBI Registration No.</span>
              <span className="wri-reg-num-val">To be updated</span>
            </div>
          </div>
        </>
      );
    }

    if (s.type === "grievance") {
      return (
        <>
          <p className="wri-text">
            Clients may raise concerns or complaints through:
          </p>
          <div className="wri-contact-grid">
            <a
              href="mailto:support@wealthcompounders.in"
              className="wri-contact-chip"
            >
              <div
                className="wri-contact-icon"
                style={{ background: "#ef444415", color: "#ef4444" }}
              >
                <i className="fas fa-envelope" />
              </div>
              <div>
                <span className="wri-contact-label">Email Us</span>
                <span className="wri-contact-val">
                  support@wealthcompounders.in
                </span>
              </div>
            </a>
            <a href="tel:+919314507135" className="wri-contact-chip">
              <div
                className="wri-contact-icon"
                style={{ background: "#ef444415", color: "#ef4444" }}
              >
                <i className="fas fa-phone" />
              </div>
              <div>
                <span className="wri-contact-label">Call Us</span>
                <span className="wri-contact-val">+91 93145 07135</span>
              </div>
            </a>
          </div>
          <div className="wri-escalate">
            <div className="wri-escalate-icon">
              <i className="fas fa-arrow-up-right-from-square" />
            </div>
            <div>
              <p className="wri-escalate-head">
                Still not satisfied? Escalate to:
              </p>
              <p className="wri-escalate-body">
                <strong>SCORES</strong> — SEBI Complaints Redress System&nbsp;
                <a
                  href="https://scores.sebi.gov.in"
                  target="_blank"
                  rel="noreferrer"
                  className="wri-scores-link"
                >
                  scores.sebi.gov.in <i className="fas fa-external-link-alt" />
                </a>
              </p>
            </div>
          </div>
        </>
      );
    }

    if (s.type === "text") {
      return <p className="wri-text">{s.content}</p>;
    }

    if (s.type === "bullets") {
      return (
        <>
          {s.intro && (
            <p className="wri-text" style={{ marginBottom: 14 }}>
              {s.intro}
            </p>
          )}
          <ul className="wri-bullets">
            {s.bullets.map((b) => (
              <li key={b} className="wri-bullet">
                <span
                  className="wri-bullet-dot"
                  style={{ background: s.color }}
                />
                {b}
              </li>
            ))}
          </ul>
          {s.note && (
            <p className="wri-note">
              <i className="fas fa-circle-info" /> {s.note}
            </p>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <div className="wri-wrapper">
      <Navbar />

      {/*  HERO  */}
      <section className="wri-hero">
        <div className="wri-hero-bg" />
        <div className="wri-hero-dotgrid" />
        <div className="wri-glow wri-glow-l" />
        <div className="wri-glow wri-glow-r" />

        <div className="wri-hero-inner">
          <div className="wri-eyebrow">
            <i className="fas fa-scale-balanced" /> Regulatory
          </div>
          <h1 className="wri-hero-title">
            Regulatory
            <br />
            <span className="wri-gradient-text">Information</span>
          </h1>
          <p className="wri-hero-sub">
            Full disclosure of our regulatory standing, compliance practices,
            and client protection measures — in line with SEBI guidelines and
            applicable Indian financial law.
          </p>
          <div className="wri-stats-strip">
            {STATS.map((s) => (
              <div className="wri-stat" key={s.label}>
                <div className="wri-stat-icon">
                  <i className={`fas ${s.icon}`} />
                </div>
                <span className="wri-stat-val">{s.val}</span>
                <span className="wri-stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/*  SPLIT PANEL  */}
      <div className="wri-split">
        <div className="wri-split-bg" />
        <div className="wri-split-inner">
          {/* LEFT: fixed TOC */}
          <aside className="wri-toc">
            <div className="wri-toc-card">
              <p className="wri-toc-heading">
                <i className="fas fa-list" /> Contents
              </p>
              <nav className="wri-toc-nav">
                {SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    className={
                      "wri-toc-item" +
                      (active === s.id ? " wri-toc-active" : "")
                    }
                    onClick={() => scrollTo(s.id)}
                    style={
                      active === s.id
                        ? { color: s.color, borderLeftColor: s.color }
                        : {}
                    }
                  >
                    <span className="wri-toc-num">{s.num}</span>
                    <span className="wri-toc-lbl">{s.title}</span>
                  </button>
                ))}
              </nav>
              <div className="wri-toc-cta">
                <p>Need clarification?</p>
                <button
                  className="wri-toc-btn"
                  onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.REGULATORY))}
                >
                  Contact Us <i className="fas fa-arrow-right" />
                </button>
              </div>
            </div>
          </aside>

          {/* RIGHT: scrollable content */}
          <main className="wri-content" ref={rightPanelRef}>
            <div className="wri-content-inner">
              {/* Intro notice */}
              <div className="wri-notice">
                <i className="fas fa-circle-info" />
                <p>
                  This page sets out the regulatory framework under which{" "}
                  <strong>Wealth Compounders</strong> operates. Please read all
                  sections carefully. For any queries, contact us directly.
                </p>
              </div>

              {SECTIONS.map((s, i) => (
                <div
                  key={s.id}
                  id={"ri-" + s.id}
                  className="wri-section"
                  style={{ animationDelay: i * 40 + "ms" }}
                >
                  <div className="wri-sec-head">
                    <div
                      className="wri-sec-icon"
                      style={{
                        background: s.color + "12",
                        border: "1.5px solid " + s.color + "22",
                        color: s.color,
                      }}
                    >
                      <i className={"fas " + s.icon} />
                    </div>
                    <div className="wri-sec-title-group">
                      <span className="wri-sec-num" style={{ color: s.color }}>
                        {s.num}
                      </span>
                      <h2 className="wri-sec-title">{s.title}</h2>
                    </div>
                    <div
                      className="wri-sec-badge"
                      style={{
                        background: s.color + "10",
                        color: s.color,
                        border: "1px solid " + s.color + "25",
                      }}
                    >
                      Section {s.num}
                    </div>
                  </div>

                  <div
                    className="wri-sec-body"
                    style={{ borderLeftColor: s.color + "45" }}
                  >
                    {renderBody(s)}
                  </div>

                  {i < SECTIONS.length - 1 && (
                    <div className="wri-sec-divider" />
                  )}
                </div>
              ))}

              {/* Bottom compliance banner */}
              <div className="wri-compliance-banner">
                <div className="wri-banner-icon">
                  <i className="fas fa-shield-halved" />
                </div>
                <div className="wri-banner-body">
                  <h3>Committed to Full Compliance</h3>
                  <p>
                    Wealth Compounders operates with full transparency and in
                    strict compliance with all applicable SEBI regulations. By
                    using our services, you acknowledge that you have read and
                    understood this regulatory information.
                  </p>
                  <div className="wri-banner-badges">
                    {["SEBI", "AMFI", "NSE", "BSE"].map((b) => (
                      <span key={b} className="wri-banner-badge">
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  className="wri-banner-btn"
                  onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.REGULATORY))}
                >
                  Speak to an Advisor <i className="fas fa-arrow-right" />
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
